import { useRef } from 'react';

type OnReady = (chart: Highcharts.Chart) => void;
type ChartLoadHandler = NonNullable<NonNullable<NonNullable<Highcharts.Options['chart']>['events']>['load']>;

type UseChartOptionsArgs = {
  onReady?: OnReady;
  options?: Highcharts.Options;
};

/**
 * Merges a consumer's Highcharts options with the Seeq `onReady` contract while
 * preserving stable options identity across rerenders when the chart
 * configuration itself has not changed.
 */
export function useChartOptions({ onReady, options }: UseChartOptionsArgs): Highcharts.Options {
  const previousOptionsRef = useRef<Highcharts.Options | undefined>(undefined);
  const mergedOptionsRef = useRef<Highcharts.Options | undefined>(undefined);
  const onReadyRef = useRef<OnReady | undefined>(onReady);
  const hadOnReadyRef = useRef(Boolean(onReady));
  const existingLoadRef = useRef<ChartLoadHandler | undefined>(undefined);
  const loadHandlerRef = useRef<ChartLoadHandler | undefined>(undefined);

  onReadyRef.current = onReady;

  const nextOptions = options ?? {};
  const hasOnReady = Boolean(onReady);
  // We can reuse the previous options if the options object is the same and the existence of an onReady callback hasn't
  // changed. This avoids unnecessary re-renders of the chart when the options haven't changed.
  const canReuseOptions = previousOptionsRef.current === nextOptions && hadOnReadyRef.current === hasOnReady;

  if (canReuseOptions && mergedOptionsRef.current) {
    return mergedOptionsRef.current;
  }

  previousOptionsRef.current = nextOptions;
  hadOnReadyRef.current = hasOnReady;

  // Create a new load handler that calls the existing load handler (if any) and then calls the onReady callback (if any).
  loadHandlerRef.current = function (this: Highcharts.Chart, event: Event) {
    existingLoadRef.current?.call(this, event);
    onReadyRef.current?.(this);
  };

  // If there is no onReady callback, we can just return the next options without modifying them.
  if (!hasOnReady) {
    mergedOptionsRef.current = nextOptions;
    existingLoadRef.current = undefined;
    return nextOptions;
  }

  existingLoadRef.current = nextOptions.chart?.events?.load;

  // Merge the passed-in `options` with the new load handler.
  mergedOptionsRef.current = {
    ...nextOptions,
    chart: {
      ...nextOptions.chart,
      events: {
        ...nextOptions.chart?.events,
        load: loadHandlerRef.current,
      },
    },
  };

  return mergedOptionsRef.current;
}
